// Copyright (C) 2020-2021 Intel Corporation
//
// SPDX-License-Identifier: MIT

import { ActionUnion, createAction, ThunkAction } from 'utils/redux';
import getCore from 'cvat-core-wrapper';

import { ShareFileInfo } from 'reducers/interfaces';

const core = getCore();

// Monotonic counter used to detect stale responses.
// Keyed by directory so that sub-directory expansions and root searches
// do not cancel each other.
let globalShareSeq = 0;
const latestShareSeqByDir = new Map<string, number>();

export enum ShareActionTypes {
    LOAD_SHARE_DATA = 'LOAD_SHARE_DATA',
    LOAD_SHARE_DATA_SUCCESS = 'LOAD_SHARE_DATA_SUCCESS',
    LOAD_SHARE_DATA_FAILED = 'LOAD_SHARE_DATA_FAILED',
}

const shareActions = {
    loadShareData: () => createAction(ShareActionTypes.LOAD_SHARE_DATA),
    loadShareDataSuccess: (values: ShareFileInfo[], directory: string) =>
        createAction(ShareActionTypes.LOAD_SHARE_DATA_SUCCESS, {
            values,
            directory,
        }),
    loadShareDataFailed: (error: any) => createAction(ShareActionTypes.LOAD_SHARE_DATA_FAILED, { error }),
};

export type ShareActions = ActionUnion<typeof shareActions>;

export function loadShareDataAsync(
    directory: string,
    success: (truncated: boolean) => void,
    failure: () => void,
    search = '',
): ThunkAction {
    return async (dispatch): Promise<void> => {
        const requestId = ++globalShareSeq;
        latestShareSeqByDir.set(directory, requestId);

        const isStale = (): boolean => latestShareSeqByDir.get(directory) !== requestId;

        try {
            dispatch(shareActions.loadShareData());
            const { data: values, truncated } = (await core.server.share(directory, search)) as {
                data: ShareFileInfo[];
                truncated: boolean;
            };
            if (isStale()) return;
            success(truncated);
            dispatch(shareActions.loadShareDataSuccess(values, directory));
        } catch (error) {
            if (isStale()) return;
            failure();
            dispatch(shareActions.loadShareDataFailed(error));
        }
    };
}
